import { hideOverlay, showOverlay } from './animator.js';
import { killMenuTitleIntro, playMenuTitleIntro } from './menuTitleIntro.js';
import {
  CHARACTERS,
  getCharacterById,
  getCharacterProgress,
  getCharacterSkillSlots,
  getSelectedCharacterId,
  getUnlockDelta,
  isCharacterUnlocked,
  setSelectedCharacterId,
} from '../characters.js';
import { animate } from 'animejs';

function callsignLabel(name) {
  const q = /"([^"]+)"/.exec(name);
  return q ? q[1] : (name.split(/\s+/)[0] ?? '').trim();
}

export class MenuController {
  constructor() {
    this.menu = document.getElementById('menu');
    this.pause = document.getElementById('pause');
    this.gameOver = document.getElementById('game-over');
    this.victory = document.getElementById('victory');
    this.goStats = document.getElementById('go-stats');
    this.loadingStatus = document.getElementById('menu-loading-status');
    this.loadingStatusText = this.loadingStatus?.querySelector('.menu-loading-text') || null;
    this.audio = null;
    this.game = null;
    this.pauseState = {
      activeTab: 'audio',
      restartConfirmUntil: 0,
      quitConfirmUntil: 0,
      restartTimer: null,
      quitTimer: null,
    };
    this._videoOptionAppliers = {};
    this._menuPopupAnimations = [];
    this.characterFlow = {
      pendingMode: 'run',
      selectedId: getSelectedCharacterId(),
      progress: getCharacterProgress(),
      detailModalOpen: false,
      selectedSkillIndex: 0,
    };
    this._onDocKeydown = this._onDocKeydown.bind(this);
    document.addEventListener('keydown', this._onDocKeydown);
    this._bindCharacterUi();
    this._bindPauseUi();
  }

  setAudio(audio) {
    this.audio = audio;
  }

  setGame(game) {
    this.game = game;
  }

  showMenu() {
    this.hideCharacterSelect();
    this.refreshCharacterProgress();
    this._show(this.menu);
    this._animateMenuButtons(this.menu, '.menu-actions button, .menu-settings-toggle');
    playMenuTitleIntro(this.menu, this.audio);
  }
  hideMenu() {
    killMenuTitleIntro(this.menu);
    this._hide(this.menu);
  }

  setLoadingState(text) {
    if (!this.loadingStatus) return;
    if (!text) {
      this.loadingStatus.hidden = true;
      if (this.loadingStatusText) this.loadingStatusText.textContent = '';
      return;
    }
    this.loadingStatus.hidden = false;
    if (this.loadingStatusText) this.loadingStatusText.textContent = text;
  }
  showPause() {
    this._refreshPauseRoomInfo();
    this._applyPauseAccent();
    this._syncPauseVideoFromPostprocess();
    this._show(this.pause);
    this._animateMenuButtons(this.pause, '.pause-actions button');
    this._setPauseVisibleState(true);
    this._focusPauseResume();
  }
  hidePause() {
    this._hide(this.pause);
    this._setPauseVisibleState(false);
    this._resetConfirmButtons();
  }
  showGameOver(room) {
    if (this.goStats) this.goStats.textContent = `Reached Room ${room} / 10`;
    this._show(this.gameOver);
    this._animateMenuButtons(this.gameOver, '#btn-retry, #btn-menu');
  }
  hideGameOver() { this._hide(this.gameOver); }
  showVictory() { this._show(this.victory); }
  hideVictory() { this._hide(this.victory); }
  refreshCharacterProgress() {
    this.characterFlow.progress = getCharacterProgress();
    this._renderCharacterSelect();
  }
  onEnemyKilled(count = 1) {
    const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    if (!safeCount) return;
    this.characterFlow.progress.totalKills += safeCount;
    this._renderCharacterSelect();
  }
  beginCharacterSelect(mode = 'run') {
    this.characterFlow.pendingMode = mode;
    this.characterFlow.selectedId = getSelectedCharacterId();
    this.characterFlow.progress = getCharacterProgress();
    this._closeCharacterModal();
    this._renderCharacterSelect();
    this.hideMenu();
    this.showCharacterSelect();
  }
  showCharacterSelect() {
    this._show(this.characterSelectRoot);
  }
  hideCharacterSelect() {
    this._closeCharacterModal();
    this._hide(this.characterSelectRoot);
  }
  getSelectedCharacterId() {
    return this.characterFlow.selectedId;
  }
  consumeCharacterStartPayload() {
    const id = this.characterFlow.selectedId;
    return {
      mode: this.characterFlow.pendingMode || 'run',
      characterId: id,
    };
  }
  isSelectedCharacterUnlocked() {
    return isCharacterUnlocked(this.characterFlow.selectedId, this.characterFlow.progress);
  }

  _show(el) {
    showOverlay(el);
  }
  _hide(el) {
    hideOverlay(el);
  }

  _onDocKeydown(ev) {
    if (!this.pause?.classList.contains('hidden')) {
      if (ev.key === 'r' || ev.key === 'R') {
        ev.preventDefault();
        ev.stopPropagation();
        this.pauseRestartBtn?.click();
      }
      return;
    }
    if (ev.key !== 'Escape') return;
    if (!this.characterSelectRoot || this.characterSelectRoot.classList.contains('hidden')) return;
    if (!this.characterFlow.detailModalOpen) return;
    ev.preventDefault();
    ev.stopPropagation();
    this._closeCharacterModal();
    this._renderCharacterSelect();
  }

  _bindPauseUi() {
    if (!this.pause) return;
    this.pauseResumeBtn = document.getElementById('btn-resume');
    this.pauseRestartBtn = document.getElementById('btn-restart-room');
    this.pauseQuitBtn = document.getElementById('btn-quit');
    this.pauseRoomMeta = document.getElementById('pause-room-meta');
    this.pauseRoomProgress = document.getElementById('pause-room-progress');

    this.pauseTabs = [...this.pause.querySelectorAll('[data-pause-tab]')];
    this.pausePanels = [...this.pause.querySelectorAll('[data-pause-panel]')];
    this.pauseTabs.forEach((tab) => {
      tab.addEventListener('click', () => this._setPauseTab(tab.dataset.pauseTab || 'audio'));
    });
    this._setPauseTab(this.pauseState.activeTab);

    this._bindPauseAudioRows();
    this._bindPauseVideoRows();
    this._bindPauseActions();
  }

  _bindPauseActions() {
    this.pauseRestartBtn?.addEventListener('click', () => {
      const now = performance.now();
      if (now <= this.pauseState.restartConfirmUntil) {
        this._resetRestartConfirm();
        this.hidePause();
        this.game?.restartCurrentRoom?.();
        return;
      }
      this._armRestartConfirm(now);
    });

    this.pauseQuitBtn?.addEventListener('click', () => {
      const now = performance.now();
      if (now <= this.pauseState.quitConfirmUntil) {
        this._resetQuitConfirm();
        this.hidePause();
        this.game?.quitRun?.();
        return;
      }
      this._armQuitConfirm(now);
    });
  }

  _bindPauseAudioRows() {
    const defaults = { master: 70, sfx: 80, music: 50 };
    const keys = {
      master: 'nihil_vol_master',
      sfx: 'nihil_vol_sfx',
      music: 'nihil_vol_music',
    };
    const rows = this.pause.querySelectorAll('[data-audio-kind]');
    rows.forEach((row) => {
      const kind = row.getAttribute('data-audio-kind');
      const input = row.querySelector('input[type="range"]');
      const valueEl = row.querySelector('[data-audio-value]');
      if (!kind || !input || !valueEl) return;
      const fromStorage = Number(localStorage.getItem(keys[kind]));
      const initial = Number.isFinite(fromStorage) ? fromStorage : defaults[kind];
      input.value = String(initial);
      valueEl.textContent = String(Math.round(initial));
      this._applyAudioSetting(kind, initial / 100);
      input.addEventListener('input', () => {
        const v = Math.max(0, Math.min(100, Number(input.value) || 0));
        valueEl.textContent = String(Math.round(v));
        this._applyAudioSetting(kind, v / 100);
        try {
          localStorage.setItem(keys[kind], String(v));
        } catch (_err) {}
      });
    });
  }

  _applyAudioSetting(kind, value) {
    if (!this.audio) return;
    if (kind === 'master') this.audio.setMasterVolume(value);
    if (kind === 'sfx') this.audio.setSfxVolume(value);
    if (kind === 'music') this.audio.setMusicVolume(value);
  }

  _bindPauseVideoRows() {
    const pixelRow = this.pause.querySelector('[data-video-kind="pixel"]');
    const bloomRow = this.pause.querySelector('[data-video-kind="bloom"]');
    this._bindVideoRow(pixelRow, 'pixelEnabled', true);
    this._bindVideoRow(bloomRow, 'bloomEnabled', true);
  }

  _bindVideoRow(row, optionKey, fallback) {
    if (!row) return;
    const btnOn = row.querySelector('[data-video-value="on"]');
    const btnOff = row.querySelector('[data-video-value="off"]');
    const current = this.game?.postprocess?.getOptions?.()?.[optionKey];
    const enabled = typeof current === 'boolean' ? current : fallback;
    const apply = (next) => {
      row.dataset.state = next ? 'on' : 'off';
      btnOn?.classList.toggle('is-active', !!next);
      btnOff?.classList.toggle('is-active', !next);
      this.game?.postprocess?.setOptions?.({ [optionKey]: !!next });
    };
    this._videoOptionAppliers[optionKey] = apply;
    apply(enabled);
    btnOn?.addEventListener('click', () => apply(true));
    btnOff?.addEventListener('click', () => apply(false));
  }

  _syncPauseVideoFromPostprocess() {
    const opts = this.game?.postprocess?.getOptions?.();
    if (!opts) return;
    for (const [optionKey, apply] of Object.entries(this._videoOptionAppliers)) {
      if (typeof apply !== 'function') continue;
      const value = opts[optionKey];
      if (typeof value === 'boolean') apply(value);
    }
  }

  _setPauseTab(tab) {
    this.pauseState.activeTab = tab;
    this.pauseTabs?.forEach((el) => {
      const active = el.dataset.pauseTab === tab;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.pausePanels?.forEach((panel) => {
      const active = panel.dataset.pausePanel === tab;
      panel.classList.toggle('hidden', !active);
    });
  }

  _refreshPauseRoomInfo() {
    const roomNumber = Math.max(1, (this.game?.roomIndex ?? 0) + 1);
    const totalRooms = Math.max(1, this.game?.totalRooms ?? 10);
    if (this.pauseRoomMeta) this.pauseRoomMeta.textContent = `ROOM ${roomNumber} · NIHIL PROJECT`;
    if (this.pauseRoomProgress) this.pauseRoomProgress.textContent = `${roomNumber} / ${totalRooms}`;
  }

  _focusPauseResume() {
    window.setTimeout(() => this.pauseResumeBtn?.focus(), 0);
  }

  _setPauseVisibleState(visible) {
    document.body.classList.toggle('pause-open', visible);
    const canvas = document.getElementById('game');
    if (canvas) canvas.style.cursor = visible ? 'default' : 'none';
  }

  _armRestartConfirm(now) {
    this.pauseState.restartConfirmUntil = now + 2000;
    this.pauseRestartBtn.textContent = 'CONFIRM RESTART';
    this.pauseRestartBtn.classList.add('is-confirming');
    if (this.pauseState.restartTimer) clearTimeout(this.pauseState.restartTimer);
    this.pauseState.restartTimer = setTimeout(() => this._resetRestartConfirm(), 2000);
  }

  _armQuitConfirm(now) {
    this.pauseState.quitConfirmUntil = now + 2000;
    this.pauseQuitBtn.textContent = 'CONFIRM QUIT';
    this.pauseQuitBtn.classList.add('is-confirming');
    if (this.pauseState.quitTimer) clearTimeout(this.pauseState.quitTimer);
    this.pauseState.quitTimer = setTimeout(() => this._resetQuitConfirm(), 2000);
  }

  _resetRestartConfirm() {
    this.pauseState.restartConfirmUntil = 0;
    if (this.pauseState.restartTimer) clearTimeout(this.pauseState.restartTimer);
    this.pauseState.restartTimer = null;
    if (!this.pauseRestartBtn) return;
    this.pauseRestartBtn.textContent = 'RESTART ROOM';
    this.pauseRestartBtn.classList.remove('is-confirming');
  }

  _resetQuitConfirm() {
    this.pauseState.quitConfirmUntil = 0;
    if (this.pauseState.quitTimer) clearTimeout(this.pauseState.quitTimer);
    this.pauseState.quitTimer = null;
    if (!this.pauseQuitBtn) return;
    this.pauseQuitBtn.textContent = 'QUIT RUN';
    this.pauseQuitBtn.classList.remove('is-confirming');
  }

  _resetConfirmButtons() {
    this._resetRestartConfirm();
    this._resetQuitConfirm();
  }

  _applyPauseAccent() {
    const color = this.game?.postprocess?.getOptions?.()?.highlightColor;
    if (color == null) return;
    let css = null;
    if (typeof color === 'number') {
      const r = (color >> 16) & 255;
      const g = (color >> 8) & 255;
      const b = color & 255;
      css = `rgb(${r}, ${g}, ${b})`;
    } else if (typeof color === 'string') {
      css = color;
    }
    if (css) this.pause.style.setProperty('--accent', css);
  }

  _closeCharacterModal() {
    this.characterFlow.detailModalOpen = false;
    if (this.characterDetailModal) this.characterDetailModal.hidden = true;
    if (this.characterModalBody) this.characterModalBody.innerHTML = '';
  }

  _bindCharacterUi() {
    this.characterSelectRoot = document.getElementById('character-select');
    this.characterGrid = document.getElementById('character-grid');
    this.characterModalBody = document.getElementById('character-modal-body');
    this.characterDetailModal = document.getElementById('character-detail-modal');
    this.characterModalBackdrop = document.getElementById('character-detail-modal-backdrop');
    this.characterConfirmBtn = document.getElementById('btn-char-select-confirm');
    this.characterBackBtn = document.getElementById('btn-char-select-back');
    this.characterModalBackBtn = document.getElementById('btn-char-modal-back');
    this.characterProgressEl = document.getElementById('character-progress-value');

    this.characterBackBtn?.addEventListener('click', () => {
      this.hideCharacterSelect();
      this.showMenu();
    });
    this.characterModalBackBtn?.addEventListener('click', () => {
      this._closeCharacterModal();
      this._renderCharacterSelect();
    });
    this.characterModalBackdrop?.addEventListener('click', () => {
      this._closeCharacterModal();
      this._renderCharacterSelect();
    });
    this.characterConfirmBtn?.addEventListener('click', () => {
      if (!this.isSelectedCharacterUnlocked()) return;
      setSelectedCharacterId(this.characterFlow.selectedId);
      this.hideCharacterSelect();
      const payload = this.consumeCharacterStartPayload();
      const event = new CustomEvent('character-select:confirm', { detail: payload });
      document.dispatchEvent(event);
    });

    this.characterDetailModal?.addEventListener('click', (e) => {
      const skillBtn = e.target.closest('[data-skill-index]');
      if (!skillBtn || !this.characterModalBody?.contains(skillBtn)) return;
      const i = parseInt(skillBtn.getAttribute('data-skill-index') || '', 10);
      if (!Number.isFinite(i)) return;
      this.characterFlow.selectedSkillIndex = Math.max(0, Math.min(2, i));
      this._renderCharacterModalContent();
    });

    this._renderCharacterSelect();
  }

  _renderCharacterSelect() {
    if (!this.characterGrid) return;
    const selected = getCharacterById(this.characterFlow.selectedId);
    if (!selected) return;

    this.characterGrid.innerHTML = '';
    for (const character of CHARACTERS) {
      const unlocked = isCharacterUnlocked(character.id, this.characterFlow.progress);
      const card = document.createElement('button');
      card.type = 'button';
      card.className = `character-card character-card--bo4 ${unlocked ? '' : 'is-locked'} ${character.id === selected.id ? 'is-selected' : ''}`;
      card.dataset.characterId = character.id;
      card.setAttribute('role', 'listitem');
      card.setAttribute('aria-label', character.name);
      const thumbSrc = character.thumbSrc || '';
      const shortName = character.name.split('"')[0].trim();
      card.innerHTML = `
        <span class="character-card-thumb-wrap">
          ${thumbSrc ? `<img class="character-card-thumb" src="${thumbSrc}" alt="" loading="lazy" draggable="false" />` : ''}
          <span class="character-card-thumb-fallback" ${thumbSrc ? 'hidden' : ''}>${character.code}</span>
        </span>
        <span class="character-card-name">${shortName}</span>
      `;
      if (!unlocked) card.setAttribute('aria-disabled', 'true');
      card.addEventListener('click', () => {
        if (!isCharacterUnlocked(character.id, this.characterFlow.progress)) return;
        this.characterFlow.selectedId = character.id;
        this.characterFlow.selectedSkillIndex = 0;
        this.characterFlow.detailModalOpen = true;
        this._renderCharacterSelect();
      });
      const img = card.querySelector('.character-card-thumb');
      const fb = card.querySelector('.character-card-thumb-fallback');
      if (img && fb) {
        img.addEventListener('error', () => {
          img.remove();
          fb.hidden = false;
        });
      }
      this._attachCardHoverAnimation(card);
      this.characterGrid.appendChild(card);
    }

    const placeholderCount = Math.max(0, 9 - CHARACTERS.length);
    for (let i = 0; i < placeholderCount; i++) {
      const lock = document.createElement('div');
      lock.className = 'character-card character-card--bo4 placeholder';
      lock.setAttribute('role', 'listitem');
      lock.innerHTML = '<span class="character-card-lock">LOCKED</span>';
      this.characterGrid.appendChild(lock);
    }

    if (this.characterProgressEl) {
      this.characterProgressEl.textContent = String(this.characterFlow.progress.totalKills);
    }

    if (this.characterFlow.detailModalOpen && this.characterDetailModal) {
      this.characterDetailModal.hidden = false;
      this._renderCharacterModalContent();
    } else if (this.characterDetailModal) {
      this.characterDetailModal.hidden = true;
    }
  }

  _renderCharacterModalContent() {
    if (!this.characterModalBody || !this.characterConfirmBtn) return;
    const selected = getCharacterById(this.characterFlow.selectedId);
    if (!selected) return;

    const slots = getCharacterSkillSlots(selected);
    const si = Math.max(0, Math.min(slots.length - 1, this.characterFlow.selectedSkillIndex));
    this.characterFlow.selectedSkillIndex = si;
    const cur = slots[si];
    const icons = selected.skillIcons || [];

    const unlockRemaining = getUnlockDelta(selected.id, this.characterFlow.progress);
    const unlocked = isCharacterUnlocked(selected.id, this.characterFlow.progress);
    const unlockLabel = unlocked ? 'Unlocked' : `Need ${unlockRemaining} more kills`;

    const tags = selected.tags.map((tag) => `<span class="character-tag">${tag}</span>`).join('');
    const idx = CHARACTERS.findIndex((c) => c.id === selected.id);

    const portraitSrc = selected.portraitSrc || '';
    const cs = callsignLabel(selected.name);

    const skillBtns = slots
      .map((s, i) => {
        const ic = icons[i];
        const label = s.section === 'ACTIVE' ? 'A' : `${i + 1}`;
        const inner = ic
          ? `<img src="${ic}" alt="" loading="lazy" draggable="false" />`
          : `<span class="character-skill-fallback">${label}</span>`;
        const ring = i === si ? ' is-current' : '';
        return `<button type="button" class="character-skill-btn${ring}" data-skill-index="${i}" aria-pressed="${i === si}" aria-label="${s.name}">${inner}</button>`;
      })
      .join('');

    this.characterModalBody.innerHTML = `
      <div class="character-modal-strip">
        <span class="character-modal-strip-context">CHOOSE CHARACTER</span>
        <h2 id="character-modal-title" class="character-modal-title">${cs}</h2>
        <span class="character-modal-strip-page">${idx + 1} / ${CHARACTERS.length}</span>
      </div>
      <div class="character-modal-columns">
        <div class="character-modal-art">
          ${portraitSrc ? `<img class="character-modal-portrait-img" src="${portraitSrc}" alt="" loading="lazy" draggable="false" />` : ''}
          <div class="character-modal-art-fallback" ${portraitSrc ? 'hidden' : ''}>${selected.code}</div>
        </div>
        <div class="character-modal-info">
          <p class="character-modal-fullname">${selected.name}</p>
          <p class="character-modal-role">${selected.role}</p>
          <blockquote class="character-modal-quote">"${selected.quote}"</blockquote>
          <div class="character-tags">${tags}</div>
          <div class="character-modal-skills-head">ABILITIES</div>
          <div class="character-skill-icons">${skillBtns}</div>
          <div class="character-skill-detail">
            <div class="character-skill-section">${cur.section}</div>
            <h3 class="character-skill-name">${cur.name}</h3>
            <p class="character-skill-text">${cur.text}</p>
          </div>
          <div class="character-unlock-note">${unlockLabel}</div>
        </div>
      </div>
    `;

    const pImg = this.characterModalBody.querySelector('.character-modal-portrait-img');
    const pFb = this.characterModalBody.querySelector('.character-modal-art-fallback');
    if (pImg && pFb) {
      pImg.addEventListener('error', () => {
        pImg.remove();
        pFb.hidden = false;
      });
    }

    for (const btn of this.characterModalBody.querySelectorAll('.character-skill-btn img')) {
      btn.addEventListener('error', () => {
        const parent = btn.closest('.character-skill-btn');
        const ix = parent ? parseInt(parent.getAttribute('data-skill-index') || '0', 10) : 0;
        const label = ix === 2 ? 'A' : `${ix + 1}`;
        btn.remove();
        const span = document.createElement('span');
        span.className = 'character-skill-fallback';
        span.textContent = label;
        parent?.appendChild(span);
      });
    }

    this.characterConfirmBtn.disabled = !unlocked;
    this.characterConfirmBtn.textContent = unlocked ? 'SELECT' : 'LOCKED';
  }

  _attachCardHoverAnimation(el) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let tween = null;
    const stopTween = () => {
      if (tween) tween.cancel();
      tween = null;
    };
    const hoverIn = () => {
      stopTween();
      tween = animate(el, {
        translateY: -6,
        scale: 1.04,
        boxShadow: '0 14px 24px rgba(228, 40, 90, 0.25)',
        duration: 240,
        ease: 'outBack(2.4)',
      });
    };
    const hoverOut = () => {
      stopTween();
      tween = animate(el, {
        translateY: 0,
        scale: 1,
        boxShadow: '0 0 0 rgba(228, 40, 90, 0)',
        duration: 140,
        ease: 'outQuad',
      });
    };
    el.addEventListener('pointerenter', hoverIn);
    el.addEventListener('pointerleave', hoverOut);
    el.addEventListener('focus', hoverIn);
    el.addEventListener('blur', hoverOut);
  }

  _clearMenuPopupAnimations() {
    for (const anim of this._menuPopupAnimations) {
      anim?.cancel?.();
    }
    this._menuPopupAnimations.length = 0;
  }

  _animateMenuButtons(root, selector) {
    if (!root || !selector) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const buttons = [...root.querySelectorAll(selector)].filter((el) => el instanceof HTMLElement);
    if (!buttons.length) return;

    this._clearMenuPopupAnimations();
    buttons.forEach((btn) => {
      btn.style.opacity = '0';
      btn.style.transformOrigin = '50% 50%';
    });

    buttons.forEach((btn, index) => {
      const anim = animate(btn, {
        opacity: [0, 1],
        translateY: [14, 0],
        scale: [0.9, 1.03, 1],
        rotateZ: ['-5deg', '0deg'],
        duration: 200,
        delay: index * 26,
        ease: 'outBack(2.2)',
      });
      this._menuPopupAnimations.push(anim);
    });
  }
}
